/**
 * Skills library — PRF-16. One flat router, `skillContract` (`/skills`), deliberately separate
 * from `competencyContract`: a different catalogue, its own `skill:read`/`skill:manage`
 * permissions (never `competency:manage` — see the plan's Scope boundary), and — unlike
 * Competency.expectedLevelByGrade — keyed by `GradeCatalogue.id` from the start rather than a
 * free-text grade code (PRF-32 exists specifically to migrate Competency away from that pattern;
 * this model never had it).
 *
 * TWO INDEPENDENT RATINGS PER (person, skill), NEVER ONE OVERWRITING THE OTHER — an employee's
 * self-rating and their manager's confirmation are separate columns on `PersonSkillRating`, the
 * same non-destructive split `ReviewCompetencyRating`'s selfRating/managerRating already uses.
 * `submitRating` is the one write endpoint for both: omit `personId` (or pass your own) to
 * self-rate, pass someone else's to confirm as their manager — the route decides which write is
 * legal from `skill:read` (self) vs `skill:manage` (someone else), never a client-supplied flag.
 *
 * LEVELS ARE POSITIONS INTO `Skill.levels`, NOT A FIXED SCALE — a 1-indexed integer into that
 * skill's own ordered label array (Beginner/Intermediate/... or whatever the admin defined), so
 * `expectedLevelByGrade` and `PersonSkillRating.selfLevel`/`managerLevel` all speak the same units
 * for that skill without assuming every skill uses the same number of levels.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs — skills
// ---------------------------------------------------------------------------

export const skillLevelsSchema = z.array(z.string().trim().min(1).max(60)).min(2).max(10);

export const skillSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string(),
  levels: skillLevelsSchema,
  /** Keyed by `GradeCatalogue.id`, valued by the expected 1-indexed level into `levels`. */
  expectedLevelByGrade: z.record(z.string(), z.number().int()),
  isActive: z.boolean(),
  createdAt: instantSchema,
});
export type Skill = z.infer<typeof skillSchema>;

export const listSkillsQuerySchema = paginationQuerySchema.extend({
  category: z.string().optional(),
  isActive: z.coerce.boolean().optional(),
});

export const createSkillBodySchema = z.object({
  code: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  category: z.string().trim().min(1).max(100),
  levels: skillLevelsSchema,
  expectedLevelByGrade: z.record(z.string(), z.number().int()).optional(),
});

export const updateSkillBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  category: z.string().trim().min(1).max(100).optional(),
  levels: skillLevelsSchema.optional(),
  expectedLevelByGrade: z.record(z.string(), z.number().int()).optional(),
});

// ---------------------------------------------------------------------------
// DTOs — ratings and gaps
// ---------------------------------------------------------------------------

export const personSkillRatingSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string(),
  skillId: uuidSchema,
  skillCode: z.string(),
  skillName: z.string(),
  skillCategory: z.string(),
  levels: skillLevelsSchema,
  selfLevel: z.number().int().nullable(),
  selfRatedAt: instantSchema.nullable(),
  managerLevel: z.number().int().nullable(),
  managerConfirmedByPersonId: uuidSchema.nullable(),
  managerConfirmedByName: z.string().nullable(),
  managerConfirmedAt: instantSchema.nullable(),
  /** Resolved from the skill's `expectedLevelByGrade` against this person's current grade — null
   *  when the person has no grade on record or the skill sets no expectation for it. */
  expectedLevel: z.number().int().nullable(),
  /** `(managerLevel ?? selfLevel) - expectedLevel`, null whenever either side is unknown. Negative
   *  is a gap; the employee-facing "My skills" view and the admin gap report share this one field
   *  rather than each recomputing it. */
  gap: z.number().int().nullable(),
  createdAt: instantSchema,
});
export type PersonSkillRating = z.infer<typeof personSkillRatingSchema>;

export const listRatingsQuerySchema = paginationQuerySchema.extend({
  /** Defaults to the caller — same "cross-record read needs skill:manage" gate every other
   *  self-vs-other endpoint in this codebase uses. */
  personId: uuidSchema.optional(),
  skillId: uuidSchema.optional(),
});

export const submitRatingBodySchema = z.object({
  /** Omitted, or your own id: a self-rating. Someone else's: a manager confirmation — requires
   *  `skill:manage` over that person. */
  personId: uuidSchema.optional(),
  skillId: uuidSchema,
  level: z.number().int().min(1),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const skillContract = c.router(
  {
    listSkills: {
      method: 'GET',
      path: '/',
      query: listSkillsQuerySchema,
      responses: {
        200: paginatedSchema(skillSchema),
        401: errorBodySchema,
      },
      summary: 'List the skill catalogue',
      metadata: routeMeta('skill:read'),
    },

    // Declared before `getSkill` — Express matches in declaration order, and `/:skillId` would
    // otherwise swallow these single-segment static paths (see contract-route-order.test.ts's
    // precedent, established for review.contract.ts's /appeals).
    listRatings: {
      method: 'GET',
      path: '/ratings',
      query: listRatingsQuerySchema,
      responses: {
        200: paginatedSchema(personSkillRatingSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A person's skill ratings — defaults to the caller's own",
      metadata: routeMeta(null),
    },

    submitRating: {
      method: 'POST',
      path: '/ratings',
      body: submitRatingBodySchema,
      responses: {
        200: personSkillRatingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Self-rate (personId omitted) or confirm a report (personId set) — which write is legal is decided server-side, never a client flag',
      metadata: routeMeta(null),
    },

    getSkill: {
      method: 'GET',
      path: '/:skillId',
      responses: {
        200: skillSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one skill',
      metadata: routeMeta('skill:read'),
    },

    createSkill: {
      method: 'POST',
      path: '/',
      body: createSkillBodySchema,
      responses: {
        201: skillSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a skill — skill:manage',
      metadata: routeMeta('skill:manage'),
    },

    updateSkill: {
      method: 'PATCH',
      path: '/:skillId',
      body: updateSkillBodySchema,
      responses: {
        200: skillSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a skill — skill:manage',
      metadata: routeMeta('skill:manage'),
    },

    archiveSkill: {
      method: 'POST',
      path: '/:skillId/archive',
      body: z.object({}),
      responses: {
        200: skillSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Archive (deactivate) a skill — skill:manage',
      metadata: routeMeta('skill:manage'),
    },
  },
  { pathPrefix: '/skills' },
);
